import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Content,
  Divider,
  EmptyState,
  EmptyStateBody,
  FormSelect,
  FormSelectOption,
  Grid,
  GridItem,
  Label,
  LabelGroup,
  Spinner,
  Title,
} from '@patternfly/react-core';
import { ArrowLeftIcon, ExternalLinkAltIcon, FilmIcon } from '@patternfly/react-icons';
import PageHeader from '@patternfly/react-component-groups/dist/dynamic/PageHeader';
import { Link, useParams } from 'react-router-dom';
import { parseAttemptComposite, parseSessionDetail as parseMonitorSessionDetail } from 'src/contracts/monitor';
import type {
  AttemptComposite,
  SessionAttempt,
  SessionDetail,
  SessionEvent,
  SessionMedia,
} from 'src/contracts/monitor';
import { useAuthedFetch } from 'src/hooks/useAuthedFetch';
import { buildClerkUserUrl } from './clerkUserLink';
import './sessions.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface TimelineEvent {
  timeMs: number;
  label: string;
  detail?: string;
}

type CompositeTemplateVersion = 'v1' | 'top_n_bottom';

const DEFAULT_COMPOSITE_TEMPLATE_VERSION: CompositeTemplateVersion = 'top_n_bottom';
const COMPOSITE_TEMPLATE_OPTIONS: Array<{ value: CompositeTemplateVersion; label: string }> = [
  { value: 'top_n_bottom', label: 'top_n_bottom' },
  { value: 'v1', label: 'v1' },
];

const formatAttemptLabel = (attemptId: string | null | undefined, attemptNumbers: Map<string, number>) => {
  if (!attemptId) {
    return 'Control';
  }
  const attemptNo = attemptNumbers.get(attemptId);
  if (typeof attemptNo !== 'number') {
    return 'Control';
  }
  return `Attempt #${attemptNo + 1}`;
};

const formatMovementDetail = (event: SessionEvent) => {
  const axes = event.payload['axes'];
  if (!Array.isArray(axes) || axes.length < 3) {
    return undefined;
  }
  const normalizedAxes = axes
    .slice(0, 3)
    .map((axis) => (typeof axis === 'number' ? axis : Number.NaN));
  if (normalizedAxes.some((axis) => Number.isNaN(axis))) {
    return undefined;
  }
  return `Axes: ${normalizedAxes.map((axis) => axis.toFixed(2)).join(', ')}`;
};

const describeControlEvent = (
  event: SessionEvent,
  attemptNumbers: Map<string, number>,
): { label: string; detail?: string } | null => {
  const attemptLabel = formatAttemptLabel(event.attempt_id, attemptNumbers);
  switch (event.event_type) {
    case 'movement_started':
      return {
        label: `${attemptLabel} movement started`,
        detail: formatMovementDetail(event),
      };
    case 'grab_triggered':
      return {
        label: `${attemptLabel} grab triggered`,
      };
    default:
      return null;
  }
};

const formatTimestamp = (value?: string | null) => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString();
};

const formatDuration = (start?: string | null, end?: string | null) => {
  if (!start) {
    return '—';
  }
  const startMs = new Date(start).getTime();
  const endMs = end ? new Date(end).getTime() : Date.now();
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
    return '—';
  }
  const delta = Math.max(0, endMs - startMs);
  const minutes = Math.floor(delta / 60000);
  const seconds = Math.floor((delta % 60000) / 1000);
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
};

const isVideo = (media: SessionMedia) => {
  if (media.content_type && media.content_type.startsWith('video/')) {
    return true;
  }
  const key = media.object_key.toLowerCase();
  return key.endsWith('.mp4') || key.endsWith('.webm') || key.endsWith('.mov');
};

const getStateColor = (state: string) => {
  switch (state.toLowerCase()) {
    case 'active':
      return 'green';
    case 'connecting':
    case 'reserved':
      return 'blue';
    case 'ended':
      return 'grey';
    case 'canceled':
    case 'expired':
      return 'red';
    default:
      return 'orange';
  }
};

const getResultColor = (kind?: string | null) => {
  if (!kind) {
    return 'grey';
  }
  switch (kind.toLowerCase()) {
    case 'win':
      return 'green';
    case 'lose':
      return 'red';
    case 'score':
      return 'blue';
    case 'time':
      return 'orange';
    default:
      return 'grey';
  }
};

const getRecordingColor = (status?: string | null) => {
  if (!status) {
    return 'grey';
  }
  switch (status.toLowerCase()) {
    case 'recording':
      return 'blue';
    case 'completed':
      return 'green';
    case 'failed':
      return 'red';
    default:
      return 'orange';
  }
};

const getCompositeColor = (status?: string | null) => {
  if (!status) {
    return 'grey';
  }
  switch (status.toLowerCase()) {
    case 'ready':
      return 'green';
    case 'queued':
      return 'blue';
    case 'rendering':
      return 'orange';
    case 'failed':
      return 'red';
    default:
      return 'grey';
  }
};

const formatDurationMs = (value?: number | null) => {
  if (typeof value !== 'number' || value <= 0) {
    return '—';
  }
  const totalSeconds = Math.floor(value / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
};

const isCompositeTemplateVersion = (value: string | null | undefined): value is CompositeTemplateVersion => {
  return value === 'v1' || value === 'top_n_bottom';
};

const getCompositeForTemplate = (
  attempt: SessionAttempt,
  templateVersion: CompositeTemplateVersion,
) => {
  return (attempt.composites || []).find((item) => item.template_version === templateVersion) || null;
};

const getDefaultCompositeTemplateVersion = (attempt: SessionAttempt): CompositeTemplateVersion => {
  if (getCompositeForTemplate(attempt, 'top_n_bottom')) {
    return 'top_n_bottom';
  }
  return DEFAULT_COMPOSITE_TEMPLATE_VERSION;
};

const getStateLabel = (value: string) => value.replace(/_/g, ' ');

const renderClerkIdentity = (externalUserId?: string | null) => {
  if (!externalUserId) {
    return '—';
  }
  const clerkUserUrl = buildClerkUserUrl(externalUserId);
  if (!clerkUserUrl) {
    return externalUserId;
  }
  return (
    <a
      className="session-clerk-link"
      href={clerkUserUrl}
      target="_blank"
      rel="noreferrer"
    >
      {externalUserId}
      <ExternalLinkAltIcon />
    </a>
  );
};

const SessionDetails = () => {
  const { sessionId } = useParams();
  const authedFetch = useAuthedFetch();
  const [session, setSession] = useState<SessionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [triggeringCompositeKey, setTriggeringCompositeKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedCompositeTemplates, setSelectedCompositeTemplates] = useState<
    Map<string, CompositeTemplateVersion>
  >(new Map());

  const fetchSession = useCallback(async () => {
    if (!sessionId) {
      return;
    }
    setLoading(true);
    try {
      const response = await authedFetch(`${API_BASE}/api/monitor/sessions/${sessionId}/?include_attempts=true`);
      if (!response.ok) {
        throw new Error(`Failed to load session (${response.status})`);
      }
      const raw = await response.json();
      const parsed = parseMonitorSessionDetail(raw);
      if (!parsed.ok) {
        throw new Error(parsed.err.message);
      }
      const data = parsed.value;
      setSession(data);
      setError(null);
      setActionError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [authedFetch, sessionId]);

  useEffect(() => {
    void fetchSession();
  }, [fetchSession]);

  const timeline = useMemo(() => {
    if (!session) {
      return [] as TimelineEvent[];
    }
    const events: TimelineEvent[] = [];
    const attemptNumbers = new Map<string, number>(
      (session.attempts || []).map((attempt) => [attempt.id, attempt.attempt_no]),
    );
    const pushEvent = (label: string, time?: string | null, detail?: string) => {
      if (!time) {
        return;
      }
      const timeMs = new Date(time).getTime();
      if (Number.isNaN(timeMs)) {
        return;
      }
      events.push({ timeMs, label, detail });
    };

    pushEvent('Session created', session.created_at, session.game_type_key || undefined);
    pushEvent('Session started', session.started_at);
    if (session.ended_at) {
      const endDetails: string[] = [];
      if (session.end_reason) {
        endDetails.push(`Reason: ${session.end_reason}`);
      }
      if (session.result?.kind) {
        endDetails.push(`Result: ${session.result.kind}`);
      }
      if (typeof session.result?.score === 'number') {
        endDetails.push(`Score: ${session.result.score}`);
      }
      pushEvent('Session ended', session.ended_at, endDetails.length ? endDetails.join(' · ') : undefined);
    }

    (session.attempts || []).forEach((attempt) => {
      pushEvent(`Attempt #${attempt.attempt_no + 1} started`, attempt.started_at);
      const detail = attempt.result?.kind ? `Result: ${attempt.result.kind}` : undefined;
      pushEvent(`Attempt #${attempt.attempt_no + 1} ended`, attempt.ended_at, detail);
    });

    (session.recording_jobs || []).forEach((job) => {
      const labelBase = `${job.camera_key || job.camera_type} ${job.media_role}`;
      pushEvent(`Recording started (${labelBase})`, job.started_at, job.status);
      pushEvent(`Recording ended (${labelBase})`, job.ended_at, job.status);
    });

    (session.session_events || []).forEach((event) => {
      const controlEvent = describeControlEvent(event, attemptNumbers);
      if (!controlEvent) {
        return;
      }
      pushEvent(controlEvent.label, event.ts, controlEvent.detail);
    });

    events.sort((a, b) => a.timeMs - b.timeMs);
    return events;
  }, [session]);

  const compositePreviews = useMemo(() => {
    if (!session?.attempts) {
      return [] as Array<{ attempt: SessionAttempt; composite: AttemptComposite }>;
    }
    return session.attempts.flatMap((attempt) =>
      (attempt.composites || []).map((composite) => ({ attempt, composite })),
    );
  }, [session]);

  const selectedTemplateForAttempt = useCallback(
    (attempt: SessionAttempt): CompositeTemplateVersion => {
      return selectedCompositeTemplates.get(attempt.id) || getDefaultCompositeTemplateVersion(attempt);
    },
    [selectedCompositeTemplates],
  );

  const updateSelectedTemplate = useCallback((attemptId: string, rawTemplateVersion: string) => {
    if (!isCompositeTemplateVersion(rawTemplateVersion)) {
      return;
    }
    setSelectedCompositeTemplates((previous) => {
      const next = new Map(previous);
      next.set(attemptId, rawTemplateVersion);
      return next;
    });
  }, []);

  const triggerComposite = useCallback(
    async (attempt: SessionAttempt, templateVersion: CompositeTemplateVersion) => {
      if (!sessionId) {
        return;
      }
      const existing = getCompositeForTemplate(attempt, templateVersion);
      const shouldForce = existing?.status === 'ready' || existing?.status === 'failed';
      const requestKey = `${attempt.id}:${templateVersion}`;
      setTriggeringCompositeKey(requestKey);
      try {
        const response = await authedFetch(
          `${API_BASE}/api/monitor/sessions/${sessionId}/attempts/${attempt.id}/composite`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              template_version: templateVersion,
              force: shouldForce,
            }),
          },
        );
        if (!response.ok) {
          throw new Error(`Composite request failed (${response.status})`);
        }
        const raw = await response.json();
        const parsed = parseAttemptComposite(raw);
        if (!parsed.ok) {
          throw new Error(parsed.err.message);
        }
        setActionError(null);
        await fetchSession();
      } catch (err) {
        setActionError((err as Error).message);
      } finally {
        setTriggeringCompositeKey(null);
      }
    },
    [authedFetch, fetchSession, sessionId],
  );

  return (
    <div className="session-detail-page">
      <PageHeader title="Session Details" subtitle={sessionId ?? ''} />

      <div className="session-detail-header">
        <Link to="/sessions" className="session-details-link">
          <Button variant="link" icon={<ArrowLeftIcon />}>
            Back to Sessions
          </Button>
        </Link>
        <Button variant="secondary" onClick={fetchSession}>
          Refresh
        </Button>
      </div>

      {actionError && (
        <EmptyState>
          <EmptyStateBody>{actionError}</EmptyStateBody>
        </EmptyState>
      )}

      {loading ? (
        <div className="session-loading">
          <Spinner size="xl" />
        </div>
      ) : error ? (
        <EmptyState>
          <Title headingLevel="h4">Unable to load session</Title>
          <EmptyStateBody>{error}</EmptyStateBody>
        </EmptyState>
      ) : session ? (
        <>
          <Grid hasGutter>
            <GridItem lg={4} sm={12}>
              <Card>
                <CardTitle>Session Summary</CardTitle>
                <CardBody>
                  <Content>
                    <p>
                      <strong>Robot:</strong> {session.robot_name || session.robot_id}
                    </p>
                    <p>
                      <strong>User:</strong> {session.user_label || session.user_id || '—'}
                    </p>
                    <p>
                      <strong>Email:</strong> {session.user_email || '—'}
                    </p>
                    <p>
                      <strong>Clerk:</strong> {renderClerkIdentity(session.clerk_external_user_id)}
                    </p>
                    <p>
                      <strong>Game:</strong> {session.game_type_key || '—'}
                    </p>
                    <p>
                      <strong>Created:</strong> {formatTimestamp(session.created_at)}
                    </p>
                    <p>
                      <strong>Started:</strong> {formatTimestamp(session.started_at)}
                    </p>
                    <p>
                      <strong>Ended:</strong> {formatTimestamp(session.ended_at)}
                    </p>
                    <p>
                      <strong>Duration:</strong> {formatDuration(session.started_at, session.ended_at)}
                    </p>
                  </Content>
                </CardBody>
              </Card>
            </GridItem>
            <GridItem lg={8} sm={12}>
              <Card>
                <CardTitle>Status</CardTitle>
                <CardBody>
                  <LabelGroup>
                    <Label color={getStateColor(session.state)}>{getStateLabel(session.state)}</Label>
                    {session.result?.kind && (
                      <Label color={getResultColor(session.result.kind)}>{session.result.kind}</Label>
                    )}
                    {session.recording_status && (
                      <Label color={getRecordingColor(session.recording_status)}>
                        Recording: {session.recording_status}
                      </Label>
                    )}
                    {typeof session.queue_position === 'number' && (
                      <Label color="blue">Queue #{session.queue_position}</Label>
                    )}
                    {session.ticket_state && <Label color="grey">Ticket: {session.ticket_state}</Label>}
                  </LabelGroup>
                  <Divider className="session-divider" />
                  <Grid hasGutter>
                    <GridItem sm={4}>
                      <div className="session-metric">
                        <small>Attempts</small>
                        <Title headingLevel="h3" size="lg">
                          {session.attempt_count ?? session.attempts?.length ?? 0}
                        </Title>
                      </div>
                    </GridItem>
                    <GridItem sm={4}>
                      <div className="session-metric">
                        <small>Result Score</small>
                        <Title headingLevel="h3" size="lg">
                          {session.result?.score ?? '—'}
                        </Title>
                      </div>
                    </GridItem>
                    <GridItem sm={4}>
                      <div className="session-metric">
                        <small>End Reason</small>
                        <Title headingLevel="h3" size="lg">
                          {session.end_reason || '—'}
                        </Title>
                      </div>
                    </GridItem>
                  </Grid>
                </CardBody>
              </Card>
            </GridItem>
          </Grid>

          <Grid hasGutter className="session-detail-grid">
            <GridItem lg={8} sm={12}>
              <Card className="session-detail-card">
                <CardTitle>Attempts</CardTitle>
                <CardBody>
                  {session.attempts && session.attempts.length > 0 ? (
                    <div className="attempt-grid">
                      {session.attempts.map((attempt) => (
                        (() => {
                          const selectedTemplateVersion = selectedTemplateForAttempt(attempt);
                          const selectedComposite = getCompositeForTemplate(attempt, selectedTemplateVersion);
                          const requestKey = `${attempt.id}:${selectedTemplateVersion}`;
                          return (
                            <div key={attempt.id} className="attempt-card">
                              <div className="attempt-header">
                                <span>#{attempt.attempt_no + 1}</span>
                                <Label color={getStateColor(attempt.state)}>{getStateLabel(attempt.state)}</Label>
                              </div>
                              <div className="attempt-meta">
                                <div>
                                  <small>Started</small>
                                  <div>{formatTimestamp(attempt.started_at)}</div>
                                </div>
                                <div>
                                  <small>Duration</small>
                                  <div>{formatDuration(attempt.started_at, attempt.ended_at)}</div>
                                </div>
                              </div>
                              <Divider />
                              <div className="attempt-result">
                                <small>Result</small>
                                {attempt.result?.kind ? (
                                  <Label color={getResultColor(attempt.result.kind)}>{attempt.result.kind}</Label>
                                ) : (
                                  <span>—</span>
                                )}
                                {typeof attempt.result?.score === 'number' && (
                                  <span className="attempt-score">Score: {attempt.result?.score}</span>
                                )}
                              </div>
                              <Divider />
                              <div className="attempt-composite-row">
                                <div className="attempt-composite-status">
                                  <small>Composite</small>
                                  {selectedComposite ? (
                                    <>
                                      <Label color={getCompositeColor(selectedComposite.status)}>
                                        {selectedComposite.status}
                                      </Label>
                                      <span className="attempt-score">{selectedComposite.template_version}</span>
                                    </>
                                  ) : (
                                    <span>Not requested</span>
                                  )}
                                </div>
                                <div className="attempt-composite-actions">
                                  <div className="attempt-template-select">
                                    <small>Template</small>
                                    <FormSelect
                                      value={selectedTemplateVersion}
                                      aria-label={`Composite template for attempt ${attempt.attempt_no + 1}`}
                                      onChange={(_event, value) => updateSelectedTemplate(attempt.id, value)}
                                    >
                                      {COMPOSITE_TEMPLATE_OPTIONS.map((option) => (
                                        <FormSelectOption
                                          key={option.value}
                                          value={option.value}
                                          label={option.label}
                                        />
                                      ))}
                                    </FormSelect>
                                  </div>
                                  <Button
                                    variant="secondary"
                                    isDisabled={
                                      triggeringCompositeKey === requestKey ||
                                      selectedComposite?.status === 'queued' ||
                                      selectedComposite?.status === 'rendering'
                                    }
                                    onClick={() => void triggerComposite(attempt, selectedTemplateVersion)}
                                  >
                                    {triggeringCompositeKey === requestKey
                                      ? 'Submitting...'
                                      : selectedComposite?.status === 'ready'
                                        ? 'Re-render composite'
                                        : 'Render composite'}
                                  </Button>
                                </div>
                              </div>
                              {selectedComposite?.error && (
                                <div className="attempt-composite-error">
                                  <small>{selectedComposite.error}</small>
                                </div>
                              )}
                            </div>
                          );
                        })()
                      ))}
                    </div>
                  ) : (
                    <EmptyState>
                      <EmptyStateBody>No attempts recorded yet.</EmptyStateBody>
                    </EmptyState>
                  )}
                </CardBody>
              </Card>

              <Card>
                <CardTitle>Composite Videos</CardTitle>
                <CardBody>
                  {compositePreviews.length > 0 ? (
                    <div className="session-media-grid">
                      {compositePreviews.map(({ attempt, composite }) => (
                        <div key={composite.id} className="session-media-card composite-card">
                          <div className="session-media-header">
                            <LabelGroup>
                              <Label color="blue">Attempt #{attempt.attempt_no + 1}</Label>
                              <Label color="grey">{composite.template_version}</Label>
                              <Label color={getCompositeColor(composite.status)}>{composite.status}</Label>
                            </LabelGroup>
                            <Badge isRead>{formatDurationMs(composite.duration_ms)}</Badge>
                          </div>
                          <div className="session-media-body">
                            {composite.composite_url ? (
                              <video className="session-media-video" controls src={composite.composite_url} />
                            ) : composite.thumbnail_url ? (
                              <img
                                className="session-media-video composite-thumbnail"
                                src={composite.thumbnail_url}
                                alt={`Composite thumbnail for attempt ${attempt.attempt_no + 1}`}
                              />
                            ) : (
                              <div className="session-media-placeholder">
                                <FilmIcon />
                                <small>No preview available</small>
                              </div>
                            )}
                          </div>
                          <div className="session-media-footer">
                            <small>
                              {composite.output_object_key || composite.thumbnail_object_key || 'Pending output key'}
                            </small>
                            {composite.composite_url ? (
                              <Button
                                variant="link"
                                component="a"
                                href={composite.composite_url}
                                target="_blank"
                                rel="noreferrer"
                                icon={<ExternalLinkAltIcon />}
                              >
                                Watch
                              </Button>
                            ) : (
                              <Label color="orange">Preview pending</Label>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState>
                      <EmptyStateBody>No composite renders requested yet.</EmptyStateBody>
                    </EmptyState>
                  )}
                </CardBody>
              </Card>

              <Card>
                <CardTitle>Videos & Recordings</CardTitle>
                <CardBody>
                  {session.media && session.media.length > 0 ? (
                    <div className="session-media-grid">
                      {session.media.map((media) => (
                        <div key={media.id} className="session-media-card">
                          <div className="session-media-header">
                            <LabelGroup>
                              <Label color="blue">{media.media_role}</Label>
                              <Label color="grey">{media.camera_key}</Label>
                              {media.status && <Label color="green">{media.status}</Label>}
                            </LabelGroup>
                            <Badge isRead>{media.storage_provider}</Badge>
                          </div>
                          <div className="session-media-body">
                            {media.media_url && isVideo(media) ? (
                              <video className="session-media-video" controls src={media.media_url} />
                            ) : (
                              <div className="session-media-placeholder">
                                <FilmIcon />
                                <small>No preview available</small>
                              </div>
                            )}
                          </div>
                          <div className="session-media-footer">
                            <small>
                              {media.bucket}/{media.object_key}
                            </small>
                            {media.media_url ? (
                              <Button
                                variant="link"
                                component="a"
                                href={media.media_url}
                                target="_blank"
                                rel="noreferrer"
                                icon={<ExternalLinkAltIcon />}
                              >
                                Watch
                              </Button>
                            ) : (
                              <Label color="orange">No public URL</Label>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState>
                      <EmptyStateBody>No recordings available yet.</EmptyStateBody>
                    </EmptyState>
                  )}
                </CardBody>
              </Card>

              <Card className="session-detail-card">
                <CardTitle>Recording Jobs</CardTitle>
                <CardBody>
                  {session.recording_jobs && session.recording_jobs.length > 0 ? (
                    <div className="recording-job-list">
                      {session.recording_jobs.map((job) => (
                        <div key={job.id} className="recording-job-row">
                          <div>
                            <Title headingLevel="h4" size="md">
                              {job.camera_key || job.camera_type}
                            </Title>
                            <small>{job.media_role}</small>
                          </div>
                          <div>
                            <Label color={getRecordingColor(job.status)}>{job.status}</Label>
                            <small>Started: {formatTimestamp(job.started_at)}</small>
                            <small>Ended: {formatTimestamp(job.ended_at)}</small>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState>
                      <EmptyStateBody>No recording jobs for this session.</EmptyStateBody>
                    </EmptyState>
                  )}
                </CardBody>
              </Card>
            </GridItem>
            <GridItem lg={4} sm={12}>
              <Card>
                <CardTitle>Session Timeline</CardTitle>
                <CardBody>
                  {timeline.length > 0 ? (
                    <div className="session-timeline">
                      {timeline.map((event) => (
                        <div key={`${event.label}-${event.timeMs}`} className="timeline-item">
                          <div className="timeline-time">{new Date(event.timeMs).toLocaleTimeString()}</div>
                          <div className="timeline-dot" />
                          <div className="timeline-content">
                            <Title headingLevel="h5" size="md">
                              {event.label}
                            </Title>
                            {event.detail && <small>{event.detail}</small>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState>
                      <EmptyStateBody>No timeline events recorded.</EmptyStateBody>
                    </EmptyState>
                  )}
                </CardBody>
              </Card>
            </GridItem>
          </Grid>
        </>
      ) : null}
    </div>
  );
};

export default SessionDetails;
